/**
 * /me/payments/archive — Session 3d PR 8.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { PayslipArchiveRow } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyPayslipArchivePage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-payslip-archive", employeeId],
    queryFn: () => session3dApi.listPayslipArchive(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<PayslipArchiveRow>[] = [
    {
      headerKey: "me.payslip-archive.col.period",
      label: "Period",
      render: (r) => <span className="font-medium">{r.period_label}</span>,
    },
    {
      headerKey: "me.payslip-archive.col.pay_date",
      label: "Pay date",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.pay_date}</span>,
    },
    {
      headerKey: "me.payslip-archive.col.gross",
      label: "Gross (SAR)",
      render: (r) => <span className="font-mono text-xs">{r.gross_sar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
    },
    {
      headerKey: "me.payslip-archive.col.deductions",
      label: "Deductions (SAR)",
      render: (r) => <span className="font-mono text-xs">{r.deductions_sar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
    },
    {
      headerKey: "me.payslip-archive.col.net",
      label: "Net (SAR)",
      render: (r) => <span className="font-mono font-semibold">{r.net_sar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>,
    },
    {
      headerKey: "me.payslip-archive.col.status",
      label: "Status",
      render: (r) => <span className="text-xs">{r.status}</span>,
    },
  ];

  return (
    <MeListPage<PayslipArchiveRow>
      title={t("me.payslip-archive.title", "Payslip Archive")}
      subtitle={t("me.payslip-archive.subtitle", "All historical payslips · downloadable · YTD totals.")}
      domain="payslip-archive"
      khidmatPrefill={t("me.payslip-archive.ask_khidmat", "Show me my pay history for the last 6 months.")}
      query={query}
      columns={columns}
      rowKey={(r) => `${r.period_label}`}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.payslip-archive.title", "Payslip Archive"),
        window_label: t("me.payslip-archive.window", "last 24 months"),
      }}
      emptyHeadline={t("me.payslip-archive.empty_headline", "No payslips on file yet.")}
      emptyBody={t("me.payslip-archive.empty_body", "Khidmat: your first payslip appears here after your first payroll run.")}
    />
  );
}
